/**
 * ScanShelveBar — interrupteur et journal du mode « scan = mise en rayon ».
 *
 * Coupé : un bouton d'armement. Armé : un bandeau bien visible (le mode déplace
 * du stock, ça ne doit jamais être discret), le compteur d'articles rangés,
 * « Annuler le dernier », et les derniers scans avec leur motif de refus.
 *
 * Toute la logique vit dans useScanShelve — ce composant ne fait que l'afficher.
 */
import { useState } from 'react';
import { Camera } from 'lucide-react';
import BarcodeScannerModal from '../ui/BarcodeScannerModal';
import { formatQty } from '../../utils/pos.utils';
import { ScanShelveCard, UseScanShelve } from '../../hooks/useScanShelve';
import { IS_TOUCH_DEVICE } from '../../utils/touchDevice';

interface ScanShelveBarProps {
  scan: UseScanShelve;
}

export default function ScanShelveBar({ scan }: ScanShelveBarProps) {
  // Caméra : c'est LE moyen de scanner en PWA sur téléphone (pas de douchette dans
  // les rayons). Mode continu — le scanner reste ouvert et enchaîne les articles.
  const [cameraOpen, setCameraOpen] = useState(false);

  if (!scan.armed) {
    return (
      <button
        type="button"
        onClick={() => void scan.arm()}
        className="w-full flex flex-wrap items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-emerald-400 text-emerald-700 font-semibold hover:bg-emerald-50 transition-colors min-h-[52px]"
      >
        ⚡ Activer « scan = mise en rayon »
        <span className="text-xs font-normal text-gray-500">
          (chaque scan range 1 unité)
        </span>
      </button>
    );
  }

  return (
    <>
      {/* Confirmation à l'écran — le bip dit qu'il s'est passé quelque chose, ce
          message dit QUOI. Fixé en haut, gros, au-dessus de tout : on scanne en
          regardant le rayon, pas la caisse. S'efface seul (2,2 s si OK, 5 s si
          refus — il y a un motif à lire). `key` force le rejeu de l'animation
          quand deux scans identiques s'enchaînent. */}
      {scan.feedback && (
        <div
          key={scan.feedback.key}
          role="status"
          aria-live="polite"
          // z-[80] : STRICTEMENT au-dessus du scanner caméra (z-[70]). En PWA la
          // caméra occupe tout l'écran — sans ça, la confirmation serait invisible
          // pendant le scan en continu, précisément quand elle est la plus utile.
          className="fixed top-3 left-1/2 -translate-x-1/2 z-[80] w-[min(92vw,32rem)] pointer-events-none"
        >
          {/* L'animation porte sur la carte, PAS sur le conteneur : le keyframe anime
              `transform`, il écraserait le -translate-x-1/2 du centrage horizontal. */}
          <div
            className={`animate-slide-down rounded-xl shadow-2xl px-5 py-4 text-center border-4 ${
              scan.feedback.ok
                ? 'bg-emerald-600 border-emerald-300 text-white'
                : 'bg-red-600 border-red-300 text-white'
            }`}
          >
            <div className="text-3xl leading-none mb-1">{scan.feedback.ok ? '✓' : '✗'}</div>
            <div className="text-lg font-bold leading-tight break-words">
              {scan.feedback.productName}
            </div>
            <div className="text-sm opacity-90 mt-0.5">{scan.feedback.message}</div>
          </div>
        </div>
      )}

      <div className="rounded-lg border-2 border-emerald-500 bg-emerald-50 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-emerald-600 text-white">
        <div className="font-bold flex items-center gap-2">
          ⚡ SCAN = MISE EN RAYON
          <span className="px-2 py-0.5 rounded bg-white/20 text-xs uppercase tracking-wide">
            {scan.loadingStock ? 'chargement…' : scan.busy ? 'transfert…' : 'actif'}
          </span>
        </div>
        <button
          type="button"
          onClick={scan.disarm}
          className="px-3 py-1.5 rounded-md bg-white/15 hover:bg-white/25 text-sm font-medium min-h-[48px]"
        >
          Arrêter
        </button>
      </div>

      {/* Deux entrées possibles, sans réglage :
          - Douchette (USB sur la caisse, ou Bluetooth appairée au téléphone) : elle
            se présente comme un CLAVIER, et l'écoute se fait au niveau du document
            — rien à focaliser, aucun champ à cliquer, ce bouton est inutile.
          - Caméra : le moyen quand on n'a que le téléphone en main. */}
      <div className="px-4 pt-3">
        <button
          type="button"
          onClick={() => setCameraOpen(true)}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-emerald-600 text-white font-bold hover:bg-emerald-700 active:bg-emerald-800 min-h-[52px]"
        >
          <Camera className="w-5 h-5" />
          Scanner avec la caméra
        </button>
        {/* Sur iPhone et iPad, l'écoute clavier est coupée par défaut — sinon le
            clavier virtuel, qui saisit par salves, passerait pour un scanner. Une
            douchette réelle doit donc être déclarée, et l'interrupteur doit être
            ICI : le réglage vit dans le stockage local du navigateur, que Safari
            efface après quelques jours sans visite. Sans lui sous la main, la
            douchette cesse de fonctionner du jour au lendemain et rien ne
            l'explique (constaté le 26/08/2026). */}
        {IS_TOUCH_DEVICE ? (
          <button
            type="button"
            onClick={scan.toggleWedge}
            className={`mt-2 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 text-sm font-semibold transition-colors min-h-[48px] ${
              scan.wedgeOnTouch
                ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                : 'border-gray-300 text-gray-600 hover:border-gray-400'
            }`}
          >
            {scan.wedgeOnTouch ? '🔌 Douchette activée' : '🔌 Activer la douchette'}
            <span className="text-xs font-normal">
              {scan.wedgeOnTouch ? '(scanne sans ouvrir la caméra)' : '(si une douchette est branchée)'}
            </span>
          </button>
        ) : (
          <p className="mt-1.5 text-center text-xs text-emerald-800">
            Douchette (USB ou Bluetooth) : scanne directement, sans ouvrir la caméra.
          </p>
        )}
      </div>

      {/* Fiche du dernier article rangé. Le bandeau du haut s'efface en 2,2 s ; ici
          l'article reste sous les yeux tant qu'on n'en scanne pas un autre — c'est
          le moment où on l'a en main et où on veut vérifier son prix ou ce qu'il
          en reste, sans aller le chercher dans la liste (demandé le 09/09/2026).
          `key` : une fiche par scan, pour que la saisie « rayon plein » reparte de
          la valeur du nouvel article et non de ce qu'on tapait sur le précédent. */}
      {scan.lastCard && (
        <LastShelvedCard
          key={scan.lastCard.key}
          card={scan.lastCard}
          saving={scan.savingCapacity}
          onSetShelfFull={scan.setShelfFull}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
        <span className="text-emerald-900 font-medium">
          {scan.shelvedCount === 0
            ? 'Scanne un article pour le mettre en rayon.'
            : `${scan.shelvedCount} article${scan.shelvedCount > 1 ? 's' : ''} rangé${scan.shelvedCount > 1 ? 's' : ''}`}
        </span>
        <button
          type="button"
          onClick={() => void scan.undoLast()}
          disabled={!scan.canUndo || scan.undoing || scan.busy}
          className="px-3 py-2 rounded-md border border-emerald-600 text-emerald-800 text-sm font-medium hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed min-h-[48px]"
        >
          {scan.undoing ? 'Annulation…' : '↩︎ Annuler le dernier'}
        </button>
      </div>

      {scan.log.length > 0 && (
        <ul className="max-h-44 overflow-y-auto divide-y divide-emerald-100 bg-white">
          {scan.log.slice(0, 8).map(entry => (
            <li
              key={entry.key}
              className={`flex items-center justify-between gap-3 px-4 py-2 text-sm ${
                entry.ok ? 'text-gray-800' : 'text-red-700 bg-red-50'
              }`}
            >
              <span className="truncate">
                {entry.ok ? '✓' : '✗'} {entry.productName}
              </span>
              <span className="flex-shrink-0 text-xs text-gray-500">
                {entry.message}
                {' · '}
                {new Date(entry.at).toLocaleTimeString('fr-FR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
      </div>

      {/* Mode continu : le scanner ne se ferme pas après chaque code, on enchaîne
          les articles. Il déduplique un même code pendant 1,5 s — pour ranger
          plusieurs paquets identiques, laisser passer ce délai entre deux. */}
      <BarcodeScannerModal
        isOpen={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onScan={scan.handleScan}
        continuous
        title="Scanner pour mettre en rayon"
      />
    </>
  );
}


/**
 * Fiche du dernier article rangé — et le seul endroit où l'on sait vraiment
 * combien tient sur l'étal.
 *
 * « Rayon plein » se mesure au moment du geste : on descend des articles jusqu'à
 * ce que la place soit prise, et à cet instant le rayon COMPTE le rayon plein.
 * Sans ce chiffre, la page des alertes propose de descendre toute la réserve ;
 * avec lui, elle dit « Ajouter 4 » parce qu'il n'y a la place que pour 4.
 * Le bouton note ce qu'il y a en rayon maintenant ; le champ sert à corriger
 * (« il en tient 12, j'en ai mis 9 »). Une valeur saisie prime pour toujours sur
 * la déduction nocturne.
 */
function LastShelvedCard({
  card,
  saving,
  onSetShelfFull,
}: {
  card: ScanShelveCard;
  saving: boolean;
  onSetShelfFull: (capacity: number | null) => void | Promise<void>;
}) {
  // Le texte est gardé tel quel pendant la frappe : une valeur contrôlée par le
  // nombre effacerait le champ dès la première touche (même piège que la saisie
  // de capacité sur la page des alertes).
  const [capaSaisie, setCapaSaisie] = useState<string>(
    card.shelfCapacity != null ? String(card.shelfCapacity) : '',
  );
  const capaConnue = card.shelfCapacity != null ? String(card.shelfCapacity) : '';
  const capaModifiee = capaSaisie.trim() !== capaConnue;

  const validerSaisie = () => {
    const brut = capaSaisie.trim().replace(',', '.');
    if (brut === '') return void onSetShelfFull(null);
    const valeur = parseFloat(brut);
    if (!Number.isFinite(valeur) || valeur < 0) return;
    void onSetShelfFull(valeur);
  };

  return (
    <div className="mx-4 mb-3 rounded-lg border border-emerald-300 bg-white overflow-hidden">
      <div className="flex gap-3 p-3">
        {card.imageUrl ? (
          <img
            src={card.imageUrl}
            alt=""
            className="w-16 h-16 rounded object-cover border border-gray-200 flex-none"
          />
        ) : (
          <div className="w-16 h-16 rounded bg-gray-50 border border-gray-200 flex-none flex items-center justify-center text-2xl">
            📦
          </div>
        )}

        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 leading-tight break-words">{card.name}</p>
          {card.categoryPath && (
            <p className="text-xs text-gray-500 truncate">{card.categoryPath}</p>
          )}
          <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="text-2xl font-bold text-emerald-700 tabular-nums leading-none">
              {formatQty(card.display)}
              <span className="text-sm font-medium text-emerald-800 ml-1">en rayon</span>
            </span>
            <span className="text-base font-semibold text-gray-700 tabular-nums">
              {formatQty(card.backroom)}
              <span className="text-sm font-normal text-gray-500 ml-1">en réserve</span>
            </span>
            {card.priceCents != null && (
              <span className="text-base font-semibold text-gray-900 tabular-nums">
                {(card.priceCents / 100).toFixed(2)} €
                {card.unit ? (
                  <span className="text-sm font-normal text-gray-500"> / {card.unit}</span>
                ) : null}
              </span>
            )}
          </div>
          {(card.expiry || card.lotNumber) && (
            <p className="mt-1.5 text-xs text-gray-500">
              {card.expiry && <>DLC {new Date(card.expiry).toLocaleDateString('fr-BE')}</>}
              {card.expiry && card.lotNumber && ' · '}
              {card.lotNumber && <>lot {card.lotNumber}</>}
            </p>
          )}
        </div>
      </div>

      {/* Rayon plein — l'information à encoder pendant qu'on est devant l'étal. */}
      <div className="border-t border-emerald-100 bg-emerald-50/70 px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <span className="font-semibold text-emerald-900">Rayon plein</span>
          {card.shelfCapacity != null ? (
            <>
              <span className="font-bold text-gray-900 tabular-nums">
                {formatQty(card.shelfCapacity)}
              </span>
              {card.shelfCapacitySource === 'manual' && (
                <span className="text-xs text-blue-600">saisi</span>
              )}
              {card.shelfCapacitySource === 'deduced' && (
                <span className="text-xs text-gray-500">déduit des mises en rayon</span>
              )}
              {card.shelfCapacitySource === 'voisins' && (
                <span className="text-xs text-amber-600">≈ d'après les voisins de rayon</span>
              )}
            </>
          ) : (
            <span className="text-gray-500">pas encore noté</span>
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {/* Le geste courant : l'étal est plein, on note ce qu'il y a dedans. */}
          <button
            type="button"
            onClick={() => void onSetShelfFull(card.display)}
            disabled={saving}
            className="flex-1 min-w-[13rem] px-3 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
          >
            {saving ? 'Enregistrement…' : `✋ C'est plein — noter ${formatQty(card.display)}`}
          </button>

          {/* Correction : ce qui TIENT peut différer de ce qu'on vient d'y mettre. */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="decimal"
              value={capaSaisie}
              onChange={e => setCapaSaisie(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }}
              onBlur={() => capaModifiee && validerSaisie()}
              placeholder="autre"
              aria-label="Rayon plein pour ce produit"
              disabled={saving}
              className="w-20 px-2 py-2 border border-gray-300 rounded-md text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-emerald-400 min-h-[48px]"
            />
            {capaModifiee && (
              <button
                type="button"
                onClick={validerSaisie}
                disabled={saving}
                className="px-3 py-2 rounded-md border border-emerald-600 text-emerald-800 text-sm font-semibold hover:bg-emerald-100 disabled:opacity-50 min-h-[48px]"
              >
                {capaSaisie.trim() === '' ? 'Effacer' : 'Enregistrer'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
